import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { Vehicle, VehiclesListResponse } from './types';
import type { VehicleFormInput } from './schemas';

interface ListParams {
  page?: number;
  limit?: number;
  q?: string;
  active?: 'true' | 'false';
  category?: string;
  sort?: string;
}

const KEY = {
  all: ['vehicles'] as const,
  list: (params: ListParams) => ['vehicles', 'list', params] as const,
  one: (id: string) => ['vehicles', 'one', id] as const,
};

export function useVehiclesList(
  params: ListParams = {},
  options?: Omit<UseQueryOptions<VehiclesListResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<VehiclesListResponse>({
    queryKey: KEY.list(params),
    queryFn: async () => {
      const { data } = await api.get<VehiclesListResponse>('/vehicles', { params });
      return data;
    },
    ...options,
  });
}

export function useVehicle(id: string | null) {
  return useQuery({
    queryKey: id ? KEY.one(id) : ['vehicles', 'one', 'null'],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<{ data: Vehicle }>(`/vehicles/${id}`);
      return data.data;
    },
  });
}

/** Calcula o measurementType a partir dos flags de tracks. */
export function getMeasurementType(
  tracksOdometer: boolean,
  tracksHourmeter: boolean,
): 'odometer' | 'hourmeter' | 'both' {
  if (tracksOdometer && tracksHourmeter) return 'both';
  if (tracksHourmeter) return 'hourmeter';
  return 'odometer';
}

function toApiPayload(input: VehicleFormInput) {
  return {
    plate: input.plate,
    brand: input.brand || undefined,
    model: input.model || undefined,
    year: input.year,
    color: input.color || undefined,
    renavam: input.renavam || undefined,
    chassi: input.chassi || undefined,
    fuelType: input.fuelType,
    category: input.category,
    // Conversão para unidades de base
    tankCapacityMl:
      input.tankCapacityL != null ? Math.round(input.tankCapacityL * 1000) : undefined,
    odometerMeters: input.odometerKm != null ? Math.round(input.odometerKm * 1000) : undefined,
    crlvNumber: input.crlvNumber || undefined,
    crlvExpiresAt: input.crlvExpiresAt || undefined,
    anttRntrc: input.anttRntrc || undefined,
    notes: input.notes || undefined,
    tracksOdometer:
      input.measurementType === 'odometer' || input.measurementType === 'both',
    tracksHourmeter:
      input.measurementType === 'hourmeter' || input.measurementType === 'both',
  };
}

export function useCreateVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: VehicleFormInput) => {
      const { data } = await api.post<{ data: Vehicle }>('/vehicles', toApiPayload(input));
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY.all });
    },
  });
}

export function useUpdateVehicle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: VehicleFormInput) => {
      const { data } = await api.patch<{ data: Vehicle }>(`/vehicles/${id}`, toApiPayload(input));
      return data.data;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: KEY.all });
      qc.setQueryData(KEY.one(id), updated);
    },
  });
}

export function useDeleteVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/vehicles/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY.all });
    },
  });
}
